/**
 * Shape of `GET /health/status` (web/health.py: HealthReader.status_payload)
 * and the rules that turn it into operator-facing severity.
 *
 * The interpretation rules live here rather than in the page so they are unit
 * testable — the interesting part of this screen is not the layout, it is
 * knowing which numbers mean the energy checkpoint writer is actually behind.
 */

/* ------------------------------------------------------------------ */
/*  Payload types                                                      */
/* ------------------------------------------------------------------ */

export interface CoreHealth {
  ok: boolean;
  pid?: number;
  uptime_s?: number;
  reason?: string | null;
}

/** Redis / Postgres: a live probe with a round-trip time. */
export interface ProbeHealth {
  ok: boolean;
  latency_ms?: number;
  reason?: string | null;
}

/** A component that publishes a heartbeat key into Redis. */
export interface HeartbeatHealth {
  ok: boolean;
  last_heartbeat_age_s?: number | null;
  pid?: number | null;
  hostname?: string | null;
  reason?: string | null;
}

export interface UdpListenerHealth extends HeartbeatHealth {
  missing_components?: string[];
}

export interface DiagnosticsLogsHealth extends HeartbeatHealth {
  last_push_age_s?: number | null;
}

export interface DiagnosticsMetricsHealth extends HeartbeatHealth {
  snapshot_age_s?: number | null;
  ttl_remaining_s?: number | null;
}

export interface EnergyWriterHealth {
  ok: boolean;
  running?: boolean;
  reason?: string | null;
  redis_version?: string | null;
  redis_supports_zadd_lt?: boolean | null;
  redis_version_checked?: boolean;
  heartbeat_age_s?: number | null;
  dirty_backlog?: number | null;
  claimed_in_flight?: number | null;
  oldest_dirty_age_ms?: number | null;
  tick_duration_ms?: number | null;
  tick_ms_p99?: number | null;
  tick_overran_interval?: boolean;
  ticks_overran?: number | null;
  rows_per_second?: number | null;
  capacity_rows_per_second?: number | null;
  capacity_rows_per_tick?: number | null;
  target_freshness_seconds?: number | null;
  projected_drain_seconds?: number | null;
  reference_fleet_units?: number | null;
  interval_seconds?: number | null;
  rows_written?: number | null;
  rows_rejected?: number | null;
  remarked_advanced?: number | null;
  released_on_failure?: number | null;
  missing_snapshots?: number | null;
  corrupt_snapshots?: number | null;
  reclaimed_expired_leases?: number | null;
  batch_ms_p50?: number | null;
  batch_ms_p99?: number | null;
  batch_ms_max?: number | null;
  ticks?: number | null;
  ticks_failed?: number | null;
}

/**
 * Hydration counters come straight out of HGETALL, so they arrive as strings.
 * `rows_failed` is not published by every writer build — absent means "this
 * deployment does not report it", never zero.
 */
export interface EnergyHydrationHealth {
  ok: boolean;
  completed?: boolean;
  completed_at?: string | null;
  started_at?: string | null;
  rows_scanned?: string | number | null;
  rows_hydrated?: string | number | null;
  rows_repaired?: string | number | null;
  rows_skipped_lower?: string | number | null;
  rows_failed?: string | number | null;
  redis_round_trips?: string | number | null;
  last_error?: string | null;
  reason?: string | null;
}

export interface HealthStatus {
  checked_at: string;
  core: CoreHealth;
  redis: ProbeHealth;
  postgres: ProbeHealth;
  udp_listener: UdpListenerHealth;
  cmd_channel_listener: HeartbeatHealth;
  diagnostics_logs: DiagnosticsLogsHealth;
  diagnostics_metrics: DiagnosticsMetricsHealth;
  energy_writer: EnergyWriterHealth;
  energy_hydration: EnergyHydrationHealth;
}

/* ------------------------------------------------------------------ */
/*  Severity                                                           */
/* ------------------------------------------------------------------ */

export type Severity = "ok" | "warning" | "critical" | "off";

export interface HealthIssue {
  level: "WARNING" | "CRITICAL";
  message: string;
}

/** Thresholds, named so the page can print what it is comparing against. */
export const HEARTBEAT_CRITICAL_AGE_S = 90;
export const OLDEST_DIRTY_WARN_MS = 120_000;
export const OLDEST_DIRTY_CRITICAL_MS = 300_000;
/** Throughput below this fraction of configured capacity is starved. */
export const THROUGHPUT_STARVED_RATIO = 0.5;
/** A backlog is only "large" relative to capacity: more than a minute of work. */
export const LARGE_BACKLOG_SECONDS_OF_CAPACITY = 60;

const RANK: Record<Severity, number> = { off: 0, ok: 1, warning: 2, critical: 3 };

export function worstSeverity(levels: Severity[]): Severity {
  return levels.reduce<Severity>((acc, s) => (RANK[s] > RANK[acc] ? s : acc), "ok");
}

export function severityOfIssues(issues: HealthIssue[]): Severity {
  if (issues.some((i) => i.level === "CRITICAL")) return "critical";
  if (issues.length > 0) return "warning";
  return "ok";
}

/** Redis HGETALL values arrive as strings; anything unparseable is "unknown". */
export function num(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/* ------------------------------------------------------------------ */
/*  Energy writer                                                      */
/* ------------------------------------------------------------------ */

/**
 * Is a checkpoint writer expected to be running in this deployment?
 *
 * `running: false` is reported both for "meter mode is off here" (fine) and for
 * "the writer died" (not fine), and the payload does not distinguish them. The
 * hydration marker does: it only exists once the energy pipeline has been rolled
 * out against this Redis, so a missing writer beside a hydration marker is a
 * stalled writer rather than a disabled feature.
 */
export function energyWriterExpected(hydration: EnergyHydrationHealth | undefined): boolean {
  if (!hydration) return false;
  return Boolean(hydration.completed || hydration.started_at || hydration.completed_at);
}

/**
 * Why an absent writer heartbeat is being read the way it is.
 *
 * The verdict above is an inference from the hydration marker, not a reading of
 * the deployment's config — /health/status carries no meter-mode or
 * PG-checkpointing flag. The operator is told which of the two it is so a wrong
 * guess is visible rather than silently trusted. Null when the writer is
 * reporting a heartbeat: there is nothing to infer.
 */
export function writerAbsenceNote(
  w: EnergyWriterHealth | undefined,
  hydration?: EnergyHydrationHealth,
): string | null {
  if (!w || w.running !== false) return null;
  return energyWriterExpected(hydration)
    ? "Writer heartbeat absent, but hydration marker exists; energy pipeline was initialized before, so this is treated as critical."
    : "Writer heartbeat absent. Treated as disabled because no hydration marker exists.";
}

export function evaluateEnergyWriter(
  w: EnergyWriterHealth | undefined,
  hydration?: EnergyHydrationHealth,
): HealthIssue[] {
  if (!w) return [];
  const issues: HealthIssue[] = [];

  if (w.ok === false) {
    issues.push({
      level: "CRITICAL",
      message: w.reason || "Energy writer reports not ok",
    });
  }

  if (w.running === false) {
    if (energyWriterExpected(hydration)) {
      issues.push({
        level: "CRITICAL",
        message:
          "No writer heartbeat, but this deployment has hydrated energy state — " +
          "the checkpoint writer is expected to be running. Nothing is draining " +
          "unit:energy:dirty into PostgreSQL.",
      });
    }
    // Otherwise the writer is simply switched off (meter mode / PG checkpointing
    // disabled). Reported as "off", not as a fault.
    return issues;
  }

  if (w.redis_supports_zadd_lt === false) {
    issues.push({
      level: "CRITICAL",
      message:
        `Redis ${w.redis_version || "(version unknown)"} does not support ZADD LT ` +
        "(needs >= 6.2) — every claim and release fails, so nothing reaches PostgreSQL.",
    });
  }

  const beat = num(w.heartbeat_age_s);
  if (beat !== null && beat > HEARTBEAT_CRITICAL_AGE_S) {
    issues.push({
      level: "CRITICAL",
      message: `Writer heartbeat is ${Math.round(beat)}s old (> ${HEARTBEAT_CRITICAL_AGE_S}s) — the writer is stalled or dead.`,
    });
  }

  // Lag, not size. The dirty ZSET score is first_dirty_at and is never refreshed
  // while a unit waits, so this is the age of the longest-waiting unit — exactly
  // the window a Redis loss would destroy. A big backlog on its own is normal
  // for a 100k fleet and is deliberately NOT an issue here.
  const oldest = num(w.oldest_dirty_age_ms);
  if (oldest !== null) {
    if (oldest > OLDEST_DIRTY_CRITICAL_MS) {
      issues.push({
        level: "CRITICAL",
        message: `Oldest dirty unit has waited ${Math.round(oldest / 1000)}s (> ${OLDEST_DIRTY_CRITICAL_MS / 1000}s) — that much accumulated energy is unprotected against a Redis loss.`,
      });
    } else if (oldest > OLDEST_DIRTY_WARN_MS) {
      issues.push({
        level: "WARNING",
        message: `Oldest dirty unit has waited ${Math.round(oldest / 1000)}s (> ${OLDEST_DIRTY_WARN_MS / 1000}s) — the writer is falling behind.`,
      });
    }
  }

  const overran = num(w.ticks_overran);
  if (w.tick_overran_interval === true || (overran !== null && overran > 0)) {
    issues.push({
      level: "WARNING",
      message:
        (w.tick_overran_interval
          ? "The last tick overran its interval"
          : `${overran} tick(s) overran their interval`) +
        " — the effective cadence is the tick's own duration, so every interval-derived number below is optimistic.",
    });
  }

  // Backlog only matters together with throughput: a large queue that capacity
  // is not being delivered against is a queue that will not drain.
  const backlog = num(w.dirty_backlog);
  const rps = num(w.rows_per_second);
  const capacity = num(w.capacity_rows_per_second);
  if (backlog !== null && rps !== null && capacity !== null && capacity > 0) {
    const largeBacklog = backlog > capacity * LARGE_BACKLOG_SECONDS_OF_CAPACITY;
    if (largeBacklog && rps < capacity * THROUGHPUT_STARVED_RATIO) {
      issues.push({
        level: "WARNING",
        message: `Backlog of ${backlog.toLocaleString()} rows is draining at ${rps.toFixed(1)} rows/s against a configured capacity of ${capacity.toFixed(1)} rows/s — the configured capacity is not being delivered.`,
      });
    }
  }

  return issues;
}

/* ------------------------------------------------------------------ */
/*  Energy hydration                                                   */
/* ------------------------------------------------------------------ */

/**
 * Hydration repairs Redis from PostgreSQL after a Redis loss. It is load-bearing
 * for the rollout gate: if the checkpoint writer is live and hydration has not
 * completed, baselines may have been seeded from zero.
 */
export function evaluateEnergyHydration(
  h: EnergyHydrationHealth | undefined,
  writer?: EnergyWriterHealth,
): HealthIssue[] {
  if (!h) return [];
  const issues: HealthIssue[] = [];

  if (h.ok === false) {
    issues.push({ level: "CRITICAL", message: h.reason || "Hydration status unreadable" });
  }

  const meterActive = writer?.running === true;
  if (meterActive && h.completed !== true) {
    issues.push({
      level: "CRITICAL",
      message:
        "The energy checkpoint writer is running but Redis has not been hydrated from " +
        "PostgreSQL" + (h.reason ? ` (${h.reason})` : "") +
        " — accumulator baselines may have been seeded from zero.",
    });
  }

  const failed = num(h.rows_failed);
  if (failed !== null && failed > 0) {
    issues.push({ level: "CRITICAL", message: `${failed} row(s) failed to hydrate.` });
  }

  if (h.last_error) {
    issues.push({ level: "CRITICAL", message: `Last hydration error: ${h.last_error}` });
  }

  return issues;
}

/* ------------------------------------------------------------------ */
/*  Generic components + overall roll-up                               */
/* ------------------------------------------------------------------ */

/**
 * Severity of a plain ok/reason component. The UDP listener additionally reports
 * sub-components: alive but incomplete is a warning, not a green light.
 */
export function componentSeverity(c: { ok?: boolean; missing_components?: string[] } | undefined): Severity {
  if (!c) return "warning";
  if (c.ok === false) return "critical";
  if (c.missing_components && c.missing_components.length > 0) return "warning";
  return "ok";
}

export function energyWriterSeverity(
  w: EnergyWriterHealth | undefined,
  hydration?: EnergyHydrationHealth,
): Severity {
  if (!w) return "warning";
  const issues = evaluateEnergyWriter(w, hydration);
  if (issues.length === 0 && w.running === false) return "off";
  return severityOfIssues(issues);
}

export function overallSeverity(s: HealthStatus | undefined): Severity {
  if (!s) return "warning";
  return worstSeverity([
    componentSeverity(s.core),
    componentSeverity(s.redis),
    componentSeverity(s.postgres),
    componentSeverity(s.udp_listener),
    componentSeverity(s.cmd_channel_listener),
    componentSeverity(s.diagnostics_logs),
    componentSeverity(s.diagnostics_metrics),
    energyWriterSeverity(s.energy_writer, s.energy_hydration),
    severityOfIssues(evaluateEnergyHydration(s.energy_hydration, s.energy_writer)),
  ]);
}
